import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import {
  ManRadioButtonGroup,
  ManRadioButton,
  ManText
} from '@craft-design-system/core-components-react';
import Shimmer_graph from '../shimmer_graph';
import { useFilterContext } from '../FilterContext';
import PopoverComponent from '../popover';
import { useResetContext } from '../ResetContext';
import useGraphRadio from '../../utils/useGraphRadio';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import AverageDisplay from '../ride_analysis_kpi/Average_value';
import DownloadButtonComponent from '../DownloadButtonComponent';
import SegmentedControl from '../ride_analysis_kpi/SegmentedControl';
import Zoom from '../Zoom';
import { CustomTextDisplay } from '../AI';
import AIToggleButton from '../AIToggleButton'; // 🔥 Added AI Toggle Button
import ifav from './ifav.png';

const Charging_Enrgy_Fast_Normal_cycle = ({ value }) => {
  const [charging_energy, set_charging_energy] = useState(null);
  const [downloadData, setDownloadData] = useState([]);
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartType, setChartType] = useState("Bar");
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const { resetdata } = useResetContext();
  const radio_query = useGraphRadio();
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🔥 AI-related state
  const [cleanedAIData, setCleanedAIData] = useState([]);
  const [radioButtons, setRadioButtons] = useState({
    'Charging Energy': 'Daily'
  });

  const [avg_value, setavg_value] = useState(
    value.chargingenergyduringfastandnormal?.[2]?.average || 0
  );

  const isBarOnly = charging_energy?.data?.every(trace => trace.type === "bar");

  // 🔥 Clean download data for AI insights
  useEffect(() => {
    if (downloadData && downloadData.length > 0) {
      const cleanData = downloadData.filter(item => 
        item && typeof item === 'object' && Object.keys(item).length > 0
      );
      setCleanedAIData(cleanData);
    }
  }, [downloadData]);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <strong>Definition:</strong><br />
        The charged energy during charging cycles is displayed distinguishing among fast charging and normal charging.<br /><br />
  
        <strong>Telematic raw data for this KPI:</strong><br />
        TBM3:<br />
        • chargedenergy<br />
        • energylevel  <br />
        CM4:<br />
        • chargedenergy   <br />
        • batchgdenergycbp<br />
        • energylevel     <br />
        • energylevelcbp  <br /><br />
  
        <strong>Time series data:</strong><br />
        The average value refers to the sum of charged energy during fast and normal charging cycles for the visualized time period.<br />
        If multiple vehicles are selected, average values are displayed. Days without data are not taken into account for the calculation.<br /><br />
  
        <strong>Histogram:</strong><br />
        Each bar represents the number of days that fall within a certain range (bin) of <strong>daily charged energy values during fast or normal charging.</strong><br />
        All daily charged energy values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
  
        <strong>Boxplot:</strong><br />
        The boxplot shows the <strong>distribution of daily charged energy values during fast or normal charging</strong> for the selected vehicle(s) since start of data acquisition.<br />
        If multiple vehicles are selected, the boxplot reflects the combined distribution of all their daily values.<br />
        • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
        • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
        • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
      </Popover.Body>
    </Popover>
  );

  const handleSelectChange1 = (event) => {
    console.log('📻 Radio button changed:', event.detail.name, event.detail.value);
    
    const newRadioValues = {
      ...radio_buttons_test,
      [event.detail.name]: event.detail.value,
    };
    set_Radio_values(newRadioValues);
    
    // 🔥 Update radio buttons state for AI insights
    setRadioButtons(newRadioValues);
  };

  // 🔥 Fetch data when chart type changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        console.log('📊 Fetching segmented chart with category:', resetdata);
        
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          category: resetdata,
          columnname: "Charging Energy",
        });

        if (chartType === "Bar") {
          set_charging_energy(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[3]));
        } else {
          set_charging_energy(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, resetdata]);

  // 🔥 Handle radio button changes - enhanced like battery temp components
  useEffect(() => {
    if (!radio_buttons_test) return;

    console.log('📻 Radio fetch triggered with:', radio_buttons_test);
    console.log('📻 Current category (resetdata):', resetdata);

    setIsLoading(true);

    // 🔥 Enhanced params to include category properly
    const params = {
      vins_short: [value.vin_short],
      radio_value: radio_buttons_test,
      columnname: "Charging Energy",
      startDate,
      endDate,
      filterdata: {
        ...filterdata,
        category: resetdata,
        resetdata: resetdata
      },
      resetdata: resetdata,
      category: resetdata,
      chartType,
    };

    console.log('📻 Sending radio query with params:', params);

    radio_query(params)
      .then(res => {
        console.log('📻 Radio query response:', res);

        // 🔥 Support BOTH response formats
        const payload = res.jsonData ?? res;
        
        if (payload) {
          set_charging_energy(JSON.parse(payload[0]));
          setavg_value(payload[2]?.average || 0);
          setDownloadData(JSON.parse(payload[3]));
        }
      })
      .catch(err => {
        console.error("❌ Radio fetch failed:", err);
      })
      .finally(() => setIsLoading(false));

  }, [radio_buttons_test, resetdata, chartType]);

  // 🔥 Initialize data from props
  useEffect(() => {
    try {
      set_charging_energy(
        JSON.parse(value.chargingenergyduringfastandnormal?.[0])
      );
      if (value.chargingenergyduringfastandnormal?.[3]) {
        setDownloadData(
          JSON.parse(value.chargingenergyduringfastandnormal[3])
        );
      }
    } catch (error) {
      console.error("Error parsing JSON", error);
    }
  }, [value]);

  // 🔥 Monitor resetdata changes
  useEffect(() => {
    console.log('🔄 resetdata changed to:', resetdata);
    
    // If we have radio buttons selected and resetdata changes, refetch
    if (radio_buttons_test && resetdata) {
      console.log('🔄 Refetching due to category change');
      // Trigger refetch by updating radio buttons state slightly
      set_Radio_values({...radio_buttons_test});
    }
  }, [resetdata]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Debug info - remove in production */}
      {/* <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        Debug: Category = {resetdata || 'None'}, Radio = {JSON.stringify(radio_buttons_test)}
      </div> */}

      {/* Title + Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '130px'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Charged energy during fast and normal charging cycle
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: 10 // 🔥 Added gap for consistent spacing
        }}>
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img 
              className="man-logovv1" 
              src={ifav} 
              alt="info" 
              style={{ 
                cursor: 'pointer',
                alignSelf: 'center' // 🔥 Ensures info icon aligns with other icons
              }} 
            />
          </OverlayTrigger>
          <Zoom
            chartData={charging_energy?.data}
            chartLayout={charging_energy?.layout}
            chartTitle="Charged energy during fast and normal charging cycle"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`charging_energy_${chartType.toLowerCase()}_${radio_buttons_test?.["Charging Energy"]?.toLowerCase() || "daily"}.xlsx`}
          />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px", marginTop: "16px" }}>
        {chartType === "Bar" ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="kWh" />
          </div>
        ) : (
          <div style={{ width: "150px" }} />
        )}
        <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          {chartType === "Bar" ? (
            <ManRadioButtonGroup
              name="Charging Energy"
              invalid="false"
              onManChange={handleSelectChange1}
              style={{ display: "flex", gap: "20px" }}
            >
              <ManRadioButton name="Charging Energy" label="Daily" value="Daily" checked />
              <ManRadioButton name="Charging Energy" label="Monthly" value="Monthly" />
              <ManRadioButton name="Charging Energy" label="Yearly" value="Yearly" />
            </ManRadioButtonGroup>
          ) : (
            <div style={{ flexGrow: 1 }} />
          )}
        </div>

        <div style={{ marginLeft: "auto" }}>
          <SegmentedControl
            selected={chartType}
            onChange={(type) => setChartType(type)}
            options={["Bar", "Histogram", "Boxplot"]}
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
  {isLoading ? (
    <Shimmer_graph />
  ) : charging_energy && charging_energy.data?.length > 0 ? (
    <>
      <Plot
        data={charging_energy.data}
        layout={{
          ...charging_energy.layout,
          margin: chartType === 'Bar' 
            ? { t: 10, r: 10, l: 10, b: 140 } // 🔥 Reduced bottom margin since AI button is moved outside
            : { t: 10, r: 10, l: 10, b: 170 },
          autosize: true,
          responsive: true,
          xaxis: {
            ...charging_energy.layout?.xaxis,
            rangeslider: { visible: false },
          },
        }}
        useResizeHandler={true}
        style={{ 
          width: "100%", 
        }}
      />

      {/* 🔥 AI Toggle Button positioned at bottom left corner */}
      {chartType === 'Bar' && cleanedAIData?.length > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-start', // 🔥 Left alignment
          marginTop: '16px' // 🔥 Space from chart
        }}>
          <AIToggleButton
            downloadData={cleanedAIData}
            radioButtonState={radioButtons}
            kpiName="Charging Energy"
            chartType={chartType}
            vinShort={value.vin_short}
            title="AI | Automotive Intelligence - Fast and Normal Charging Energy Analysis"
          />
        </div>
      )}
    </>
  ) : (
    <div style={{ textAlign: 'center', color: 'gray', fontSize: '16px', marginTop: '40px' }}>
      No data available for current filter
    </div>
  )}
</div>
    </div>
  );
};

Charging_Enrgy_Fast_Normal_cycle.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    chargingenergyduringfastandnormal: PropTypes.array.isRequired,
  }).isRequired,
};

export default Charging_Enrgy_Fast_Normal_cycle;